/**
 * Openings — adding, moving and resizing windows and doors.
 *
 * The counterpart of edits.ts / dragMath.ts for the third layer of a surface.
 * An opening cuts through whatever product is under it, so it never collides
 * with sections; what it must respect is the wall itself:
 *
 *   - it stays inside the wall, at least the SAFE MARGIN from the perimeter
 *     (an admin setting, 4" by default) — except that doors and garage doors
 *     stand on the floor, so their bottom edge is exempt, and is fixed there
 *     (until one is unpinned: then it is placed freely, like a window — though
 *     its bottom edge stays exempt, so it can still come down to the floor);
 *   - openings never overlap each other.
 *
 * The rules themselves live in validation.ts (`checkOpeningPlacement`); every
 * function here goes through them and returns a new room or the reason it was
 * refused, exactly like the section edits.
 */

import { Opening, OpeningFloor, OpeningKind, RoomSurfaces, Surface, openingKeepsOffFloor, openingOnFloor, openingSitsOnFloor, replaceRoomSurface, roomSurfaceById } from '../../types/sections';
import { Rect, intersectRect } from './resolver';
import {
  MIN_OPENING_SIZE_FT,
  PlacementIssue,
  checkOpeningPlacement,
  findOpening,
  openingRect,
  validateSurface,
} from './validation';
import { DragFrame, DragResult, Handle, computeCandidate, fitToSurface } from './dragMath';
import { EditFailure, EditResult } from './edits';

export interface OpeningRules {
  /** Safe margin from the wall's perimeter, in feet. */
  margin: number;
}

const NO_MARGIN: OpeningRules = { margin: 0 };

/** Starting size (feet) and sill height for a new opening of each kind. */
export const OPENING_DEFAULTS: Record<OpeningKind, { width: number; height: number; y: number }> = {
  window: { width: 3, height: 4, y: 3 },
  door: { width: 3, height: 80 / 12, y: 0 }, // 3'0" x 6'8"
  garageDoor: { width: 9, height: 7, y: 0 },
  passage: { width: 3, height: 7, y: 0 }, // never added by hand
};

const fail = (reason: EditFailure): EditResult => ({ ok: false, reason });

const surfaceOf = roomSurfaceById;
const withSurface = replaceRoomSurface;

/** An opening id not yet used on the surface, e.g. `wall1-window-2`. */
export function nextOpeningId(surface: Surface, kind: OpeningKind): string {
  const stem = `${surface.id}-${kind === 'garageDoor' ? 'garage' : kind}`;
  const used = new Set([...(surface.openings ?? []).map((o) => o.id), ...surface.sections.map((s) => s.id)]);
  let n = (surface.openings ?? []).length + 1;
  while (used.has(`${stem}-${n}`)) n++;
  return `${stem}-${n}`;
}

/**
 * The allowed spot for a `width` x `height` opening nearest to (`x`, `y`), or
 * null when the wall has no room for it.
 *
 * Candidates are the positions that put it at the preferred place, hard against
 * a margin, or one margin clear of another opening — free space for an opening
 * always touches one of those.
 */
export function findOpeningSpot(
  surface: Surface,
  kind: OpeningKind | OpeningFloor,
  size: { width: number; height: number },
  prefer: { x: number; y: number },
  rules: OpeningRules = NO_MARGIN,
  ignoreId?: string,
): Rect | null {
  const { width, height } = size;
  const m = rules.margin;
  const others = (surface.openings ?? []).filter((o) => o.id !== ignoreId).map(openingRect);

  const xs = new Set<number>([prefer.x, m, surface.width - m - width, (surface.width - width) / 2]);
  for (const o of others) { xs.add(o.x + o.width + m); xs.add(o.x - width - m); }
  const onFloor = openingOnFloor(kind);
  const ys = new Set<number>(onFloor ? [0] : [prefer.y, openingKeepsOffFloor(kind) ? m : 0, surface.height - m - height]);
  if (!onFloor) for (const o of others) { ys.add(o.y + o.height + m); ys.add(o.y - height - m); }

  let best: Rect | null = null;
  let bestDist = Infinity;
  xs.forEach((x) => ys.forEach((y) => {
    const rect = { x, y, width, height };
    if (checkOpeningPlacement(surface, rect, kind, { margin: m, ignoreId })) return;
    // Same height first (a copy lands beside the original), then nearest — to the right on a tie.
    const dist = (Math.abs(y - prefer.y) > 1e-9 ? 1e6 : 0) + Math.abs(x - prefer.x) + Math.abs(y - prefer.y) + (x < prefer.x ? 1e-6 : 0);
    if (dist < bestDist) { bestDist = dist; best = rect; }
  }));
  return best;
}

export type OpeningResult = { ok: true; room: RoomSurfaces; openingId: string } | { ok: false; reason: EditFailure };

/** Add a new window or door at its default size, centred on the wall if there is room there. */
export function addOpening(room: RoomSurfaces, surfaceId: string, kind: OpeningKind, rules: OpeningRules = NO_MARGIN): OpeningResult {
  const surface = surfaceOf(room, surfaceId);
  if (!surface) return { ok: false, reason: 'not-found' };
  if (surface.kind === 'ceiling') return { ok: false, reason: 'opening-on-ceiling' };
  const d = OPENING_DEFAULTS[kind];
  // Shrink the default to the wall when the wall is smaller than it.
  const width = Math.min(d.width, surface.width - 2 * rules.margin);
  const height = Math.min(d.height, surface.height - rules.margin - (openingSitsOnFloor(kind) ? 0 : rules.margin));
  if (width < MIN_OPENING_SIZE_FT || height < MIN_OPENING_SIZE_FT) return { ok: false, reason: 'no-room' };
  const spot = findOpeningSpot(surface, kind, { width, height }, { x: (surface.width - width) / 2, y: Math.min(d.y, surface.height - rules.margin - height) }, rules);
  if (!spot) return { ok: false, reason: 'no-room' };
  const opening: Opening = { id: nextOpeningId(surface, kind), kind, ...spot };
  return { ok: true, room: withSurface(room, { ...surface, openings: [...(surface.openings ?? []), opening] }), openingId: opening.id };
}

/** Move / resize an opening, or change its trim. */
export function updateOpening(
  room: RoomSurfaces,
  surfaceId: string,
  openingId: string,
  patch: Partial<Omit<Opening, 'id' | 'kind'>>,
  rules: OpeningRules = NO_MARGIN,
): EditResult {
  const found = findOpening(room, surfaceId, openingId);
  if (!found) return fail('not-found');
  if (found.opening.kind === 'passage') return fail('passage-fixed');
  const next: Opening = { ...found.opening, ...patch };
  const issue = checkOpeningPlacement(found.surface, openingRect(next), next, { margin: rules.margin, ignoreId: openingId });
  if (issue) return fail(issue);
  return {
    ok: true,
    room: withSurface(room, { ...found.surface, openings: (found.surface.openings ?? []).map((o) => (o.id === openingId ? next : o)) }),
  };
}

/** Copy an opening into the nearest spot that has room; `no-room` when none does. */
export function duplicateOpening(room: RoomSurfaces, surfaceId: string, openingId: string, rules: OpeningRules = NO_MARGIN): OpeningResult {
  const found = findOpening(room, surfaceId, openingId);
  if (!found) return { ok: false, reason: 'not-found' };
  const { surface, opening } = found;
  const spot = findOpeningSpot(surface, opening, opening, opening, rules);
  if (!spot) return { ok: false, reason: 'no-room' };
  if (opening.kind === 'passage') return { ok: false, reason: 'passage-fixed' };
  // (A copy is a new opening of its own: it does not share the original's link.)
  const copy: Opening = { ...opening, ...spot, id: nextOpeningId(surface, opening.kind), link: undefined };
  return { ok: true, room: withSurface(room, { ...surface, openings: [...(surface.openings ?? []), copy] }), openingId: copy.id };
}

/**
 * Move several things on one surface in a single step — sections and openings
 * alike (align / distribute). Everything is checked against the surface as it
 * would be AFTERWARDS; if anything breaks a rule, nothing is changed.
 */
export function updateItems(
  room: RoomSurfaces,
  surfaceId: string,
  patches: Record<string, Partial<Rect>>,
  rules: OpeningRules = NO_MARGIN,
): EditResult {
  const surface = surfaceOf(room, surfaceId);
  if (!surface) return fail('not-found');
  const known = new Set([...surface.sections.map((s) => s.id), ...(surface.openings ?? []).map((o) => o.id)]);
  if (Object.keys(patches).some((id) => !known.has(id))) return fail('not-found');
  if ((surface.openings ?? []).some((o) => o.kind === 'passage' && patches[o.id])) return fail('passage-fixed');

  const next: Surface = {
    ...surface,
    sections: surface.sections.map((s) => (patches[s.id] ? { ...s, ...patches[s.id] } : s)),
    ...(surface.openings ? { openings: surface.openings.map((o) => (patches[o.id] ? { ...o, ...patches[o.id] } : o)) } : {}),
  };

  // Moved openings must also respect the safe margin (validateSurface does not apply it).
  for (const o of next.openings ?? []) {
    if (!patches[o.id]) continue;
    const issue = checkOpeningPlacement(next, openingRect(o), o, { margin: rules.margin, ignoreId: o.id });
    if (issue) return fail(issue);
  }
  const issues = validateSurface(next);
  const issue = issues.find((i) => patches[i.sectionId]) ?? issues[0];
  if (issue) return fail(issue.issue);
  return { ok: true, room: withSurface(room, next) };
}

/**
 * Pin a door or garage door to the floor, or let it go.
 *
 * Unpinned, it stays where it is and can then be moved up and down like a
 * window (the safe margin still does not apply to its bottom edge, so it can
 * come back down to the floor). Pinned again, it drops straight down; refused
 * if something is in the way.
 */
export function setOpeningFloorPinned(
  room: RoomSurfaces,
  surfaceId: string,
  openingId: string,
  pinned: boolean,
  rules: OpeningRules = NO_MARGIN,
): EditResult {
  const found = findOpening(room, surfaceId, openingId);
  if (!found) return fail('not-found');
  if (!openingSitsOnFloor(found.opening.kind) || found.opening.kind === 'passage') return fail('not-found');
  return updateOpening(room, surfaceId, openingId, pinned ? { floorPinned: undefined, y: 0 } : { floorPinned: false }, rules);
}

// ---------------------------------------------------------------------------
// Nudging (arrow keys)
// ---------------------------------------------------------------------------

const INCH = 1 / 12;
const rectFrom = (r: Rect): Rect => ({ x: r.x, y: r.y, width: r.width, height: r.height });

/**
 * Move the given sections / openings together by one step along x or y, as far
 * as the rules allow.
 *
 * A nudge never breaks a rule: it stops at the wall's edge (sections) or the
 * safe margin (openings), and against anything it may not overlap — so the
 * last press before an obstacle is a shorter one that lands exactly on it. A
 * door pinned to the floor slides sideways only. Nothing moves, and the reason
 * comes back, when there is no room at all in that direction.
 */
export function nudgeItems(
  room: RoomSurfaces,
  surfaceId: string,
  ids: string[],
  dx: number,
  dy: number,
  rules: OpeningRules = NO_MARGIN,
): EditResult {
  const surface = surfaceOf(room, surfaceId);
  if (!surface || ids.length === 0) return fail('not-found');
  const sections = surface.sections.filter((s) => ids.includes(s.id));
  const openings = (surface.openings ?? []).filter((o) => ids.includes(o.id));
  if (sections.length + openings.length !== ids.length) return fail('not-found');
  const vertical = dy !== 0;
  const step = Math.abs(vertical ? dy : dx);
  const sign = Math.sign(vertical ? dy : dx);
  if (step === 0) return fail('not-found');
  if (openings.some((o) => o.kind === 'passage')) return fail('passage-fixed');
  if (vertical && openings.some((o) => openingOnFloor(o))) return fail('not-on-floor');
  const shifted = (r: Rect, by: number): Rect => (vertical ? { ...r, y: r.y + sign * by } : { ...r, x: r.x + sign * by });
  const travel = (from: Rect, to: Rect) => Math.abs(vertical ? to.y - from.y : to.x - from.x);

  // How far each moving item can go before its own limit: the wall for a
  // section, the safe margin for an opening.
  let reach = step;
  for (const section of sections) {
    const rect = rectFrom(section);
    const fitted = fitToSurface(shifted(rect, step), surface, true);
    if (!fitted) return fail('out-of-bounds');
    reach = Math.min(reach, travel(rect, fitted));
  }
  for (const opening of openings) {
    const rect = rectFrom(opening);
    const fitted = fitOpening(shifted(rect, step), surface, opening, rules, true);
    if (!fitted) return fail('too-close-to-edge');
    reach = Math.min(reach, travel(rect, fitted));
  }

  // Distances to try, longest first: the full step (or as far as the limits
  // allow), then flush against each thing standing in the way, then inch by inch.
  const tries = new Set<number>([reach]);
  const moving: Rect[] = [...sections, ...openings].map(rectFrom);
  const others: Rect[] = [...surface.sections, ...(surface.openings ?? [])].filter((o) => !ids.includes(o.id)).map(rectFrom);
  for (const m of moving) {
    for (const o of others) {
      const gap = vertical
        ? (sign > 0 ? o.y - (m.y + m.height) : m.y - (o.y + o.height))
        : (sign > 0 ? o.x - (m.x + m.width) : m.x - (o.x + o.width));
      if (gap > 1e-6 && gap < reach) tries.add(gap);
    }
  }
  for (let d = Math.floor(reach / INCH + 1e-6) * INCH; d > 1e-6; d -= INCH) {
    if (d < reach - 1e-6) tries.add(d);
  }

  let refused: EditResult = fail(openings.length > 0 ? 'too-close-to-edge' : 'out-of-bounds');
  for (const distance of [...tries].filter((d) => d > 1e-6).sort((a, b) => b - a)) {
    const patches: Record<string, Partial<Rect>> = {};
    for (const item of [...sections, ...openings]) {
      patches[item.id] = vertical ? { y: item.y + sign * distance } : { x: item.x + sign * distance };
    }
    const result = updateItems(room, surfaceId, patches, rules);
    if (result.ok) return result;
    if (distance === reach) refused = result;
  }
  return refused;
}

/** Remove sections and / or openings from one surface in a single step. */
export function deleteItems(room: RoomSurfaces, surfaceId: string, ids: string[]): EditResult {
  const surface = surfaceOf(room, surfaceId);
  if (!surface) return fail('not-found');
  const doomed = new Set(ids);
  const known = new Set([...surface.sections.map((s) => s.id), ...(surface.openings ?? []).map((o) => o.id)]);
  if (ids.some((id) => !known.has(id))) return fail('not-found');
  if ((surface.openings ?? []).some((o) => o.kind === 'passage' && doomed.has(o.id))) return fail('passage-fixed');
  return {
    ok: true,
    room: withSurface(room, {
      ...surface,
      sections: surface.sections.filter((s) => !doomed.has(s.id)),
      ...(surface.openings ? { openings: surface.openings.filter((o) => !doomed.has(o.id)) } : {}),
    }),
  };
}

// ---------------------------------------------------------------------------
// Dragging
// ---------------------------------------------------------------------------

/**
 * Keep an opening inside the wall's safe margins. A move keeps its size and is
 * pushed back inside; a resize is clipped. Doors and garage doors are held on
 * the floor either way, unless unpinned. Null when it cannot fit at all.
 */
export function fitOpening(rect: Rect, surface: Surface, kind: OpeningKind | OpeningFloor, rules: OpeningRules, isMove: boolean): Rect | null {
  const m = rules.margin;
  const onFloor = openingOnFloor(kind);
  const minX = m;
  const maxX = surface.width - m;
  const minY = openingKeepsOffFloor(kind) ? m : 0;
  const maxY = surface.height - m;
  let { x, y, width, height } = rect;

  if (onFloor) {
    // The bottom edge never leaves the floor: a move slides sideways only, and
    // dragging the bottom edge does nothing.
    if (isMove) y = 0;
    else { height = y + height; y = 0; }
  }
  if (isMove) {
    if (width > maxX - minX + 1e-9 || height > maxY - minY + 1e-9) return null;
    x = Math.min(Math.max(x, minX), maxX - width);
    y = Math.min(Math.max(y, minY), maxY - height);
  } else {
    if (x < minX) { width -= minX - x; x = minX; }
    if (y < minY) { height -= minY - y; y = minY; }
    if (x + width > maxX) width = maxX - x;
    if (y + height > maxY) height = maxY - y;
  }
  if (width < MIN_OPENING_SIZE_FT || height < MIN_OPENING_SIZE_FT) return null;
  return { x, y, width, height };
}

/**
 * One step of dragging an opening: where the pointer asks it to go (with
 * snapping) → held inside the margins → allowed? Mirrors `resolveDrag`.
 */
export function resolveOpeningDrag(
  surface: Surface,
  opening: { id: string } & OpeningFloor,
  handle: Handle,
  orig: Rect,
  dxFt: number,
  dyFt: number,
  frame: DragFrame,
  rules: OpeningRules = NO_MARGIN,
): DragResult {
  const candidate = computeCandidate(handle, orig, dxFt, dyFt, frame);
  const fitted = fitOpening(candidate, surface, opening, rules, handle === 'move');
  if (!fitted) return { ok: false, issue: 'too-small' };
  const issue: PlacementIssue | null = checkOpeningPlacement(surface, fitted, opening, { margin: rules.margin, ignoreId: opening.id });
  return issue ? { ok: false, issue } : { ok: true, rect: fitted };
}

/** Does this rectangle touch any opening? (Used to keep new sections' "+" spots honest.) */
export function overlapsOpening(surface: Surface, rect: Rect): boolean {
  return (surface.openings ?? []).some((o) => intersectRect(rect, openingRect(o)));
}
